import { MathUtil } from "@base/math/MathUtil";
import { EntryProcessor, stringifySafe } from "./stringifySafe";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import reduce from "lodash/reduce";
import throttle from "lodash/throttle";

type INewable<T> = new (...params: any[]) => T;

export function getProperty<T, K extends keyof T>(o: T, name: K): T[K] {
  return o[name]; // o[name] is of type T[K]
}

export function setProperty<T, K extends keyof T>(o: T, name: K, value: T[K]): void {
  o[name] = value; // o[name] is of type T[K]
}

export function property<T, K extends keyof T>(o: T, name: K): { get: () => T[K]; set: (value: T[K]) => void; name: string } {
  return {
    get: () => getProperty(o, name),
    set: (value: T[K]) => setProperty(o, name, value),
    name: "" + name.toString(),
  };
}

export function asValue<T, K extends keyof T>(o: T, name: K, onChange?: (value: T[K]) => void): { value: T[K]; onChange: (value: T[K]) => void } {
  const defaultOnChange = (value: T[K]): T[K] => (o[name] = value);
  return {
    value: o[name],
    onChange: onChange
      ? (value: T[K]) => {
        defaultOnChange(value);
        onChange(value);
      }
      : defaultOnChange,
  };
}

export function asValueDefault<T, K extends keyof T>(o: T, name: K, defaultValue: T[K], onChange?: (value: T[K]) => void): { value: T[K]; onChange: (value: T[K] | undefined) => void; key: string } {
  const defaultOnChange = (value: T[K]): T[K] => (o[name] = value);
  return {
    value: o[name],
    onChange: onChange
      ? (value: T[K] | undefined) => {
        if (value === undefined) value = defaultValue;
        defaultOnChange(value);
        onChange(value);
      }
      : (value: T[K] | undefined) => {
        if (value === undefined) value = defaultValue;
        defaultOnChange(value);
      },
    key: "" + name.toString(),
  };
}

export function propertyDefault<T, K extends keyof T>(o: T, name: K, defaultValue: T[K]): { get: () => T[K]; set: (value: T[K] | undefined) => void; name: string } {
  return {
    get: () => getProperty(o, name),
    set: (value: T[K] | undefined) => (value === undefined ? setProperty(o, name, defaultValue) : setProperty(o, name, value)),
    name: "" + name.toString(),
  };
}

// const t = {test: ""};
// const t2 = () => property(t, "test");
export interface IPropertyType<T> {
  get: () => T;
  set: (val: T) => void;
  name: string;
}

export class Util {
  static isA<T>(obj: any, constructor: INewable<T>): boolean {
    return obj instanceof constructor;
  }

  static getA<T>(obj: any, constructor: INewable<T>): T | null {
    if (!Util.isA<T>(obj, constructor)) return null;
    return obj as T;
  }

  static isPrimitive(test: any): boolean {
    return test !== Object(test);
  }

  // 6.1.7 The Object Type
  // https://tc39.github.io/ecma262/#sec-object-type
  static isObject<T>(x: T | undefined | null | boolean | string | symbol | number): x is T {
    return typeof x === "object" ? x !== null : typeof x === "function";
  }

  static isString(object: any): object is string {
    return typeof object === "string";
  }

  static isNumber(object: any): object is number {
    return typeof object === "number";
  }

  static isNumeric(n: any): n is number | string {
    return !isNaN(parseFloat(n)) && isFinite(n);
  }

  static toNumber(newVal: any): number {
    if (Util.isString(newVal)) return parseFloat(newVal);
    else if (Util.isNumber(newVal)) return newVal;
    return 0;
  }

  static toIntNumber(newVal: any, radix?: number): number {
    if (!radix) radix = 10;
    if (Util.isString(newVal)) return parseInt(newVal, radix);
    else if (Util.isNumber(newVal)) return newVal;
    return 0;
  }

  static isFunction(object: any): object is ((...params: any[]) => any) {
    const getType = {};
    return object && getType.toString.call(object) === "[object Function]";
  }

  static capitalizeFirstLetter(str: string): string {
    if (!str)
      return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  static capitalizeFirstLetters(str: string): string {
    if (!str)
      return str;
    let upper = str.charAt(0).toUpperCase() + str.slice(1);
    let current = 0;
    while (current !== -1) {
      current = upper.indexOf("_", current + 1)
      if (current !== -1) {
        const ch = upper.charAt(current + 1);
        const uc = ch.toUpperCase();
        if (ch !== uc) {
          upper = upper.slice(0, current + 1) + uc + upper.slice(current + 2);
        }
      }
    }
    return upper;
  }

  static parseQueryParams(qs: string): any {
    qs = qs.split("+").join(" ");

    const params: any = {};
    const re = /[?&]?([^=]+)=([^&]*)/g;
    let tokens = re.exec(qs);

    while (tokens) {
      params[decodeURIComponent(tokens[1])] = decodeURIComponent(tokens[2]);
      tokens = re.exec(qs);
    }

    return params;
  }

  static isValidEmail(email: string | undefined): boolean {
    if (!email)
      return false;
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  }

  static classNames(...args: any): string {
    let classes: string[] = [];

    for (const a of args) {
      if (a !== undefined) {
        if (Util.isString(a)) classes.push(a);
        else if (Array.isArray(a) && a.length > 0) {
          const arr = a as string[];
          classes = classes.concat(arr);
        } else if (typeof a === "object") {
          for (const key in a) {
            if (a) {                            
              if (Object.prototype.hasOwnProperty.call(a, key) && a[key]) {
                classes.push(key);
              }
            }
          }
        }
      }
    }
    return classes.join(" ");
  }

  static px(size: number): string {
    return size + "px";
  }

  static parseColor(color: string): number {
    if (color == null || color.length === 0) {
      return 0xffffffff;
    }
    if (color.startsWith("#")) {
      color = color.substr(1);
    }
    const val = parseInt(color, 16);
    return val;
  }

  static componentToHex(c: number): string {
    return this.byteToHex(c);
  }

  static byteToHex(byte: number): string {
    return ("0" + (byte & 0xff).toString(16)).slice(-2);
  }

  static rgbToHex(r: number, g: number, b: number): string {
    return "#" + Util.componentToHex(r) + Util.componentToHex(g) + Util.componentToHex(b);
  }

  static replaceErrors(_key: string, _value: any): any {
    if (_value instanceof Error) {
      const error: any = {};

      Object.getOwnPropertyNames(_value).forEach((key) => {
        error[key] = (_value as any)[key];
      });

      return error;
    }

    return _value;
  }

  static stringify(obj: any, serializer?: EntryProcessor | null, indent?: number): string {
    if (indent === undefined) indent = 2;
    return stringifySafe(obj, serializer, 2);
  }

  static getVariableName<TResult>(name: () => TResult): string {
    const varExtractor = /\(\) => (.*\.)*(.+)/g;
    const nameString = name + "";
    const m = varExtractor.exec(nameString);
    if (m == null) throw new Error("The function does not contain a statement matching 'return variableName;'");
    return m[m.length - 1];
  }

  static asString(pathes: any): string {
    // Query not supported for the moment
    if (Array.isArray(pathes)) {
      return (pathes as string[])[0];
    }
    return pathes as string;
  }

  static asInt(val: any): number {
    if (Array.isArray(val)) val = val[0];
    if (this.isNumber(val)) return val;
    if (this.isString(val)) return parseInt(val, 10);
    throw new Error("Invalid number!");
  }

  static ensureArray<T>(obj: T | T[]): T[] {
    if (Array.isArray(obj)) {
      return obj;
    } else {
      if (obj === undefined) return [];
      return [obj];
    }
  }

  static isEmpty(obj: any): boolean {
    return Object.keys(obj as object).length === 0 && obj.constructor === Object;
  }

  static getTodayTimestamp(): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.valueOf();
  }

  static getTimestampWithAge(days: number, hours?: number, minutes?: number, seconds?: number): number {
    const timestamp = Date.now() - this.getTimestampDuration(days, hours, minutes, seconds);
    return timestamp;
  }

  static getTimestampDuration(days: number, hours?: number, minutes?: number, seconds?: number): number {
    let duration = days * 24 * 60 * 60 * 1000;
    if (hours !== undefined) duration += hours * 60 * 60 * 1000;
    if (minutes !== undefined) duration += minutes * 60 * 1000;
    if (seconds !== undefined) duration += seconds * 1000;
    return duration;
  }

  static toPosix(path: string): string {
    return path.split("\\").join("/");
  }

  static round(num: number, decimals: number): number {
    if (!isNaN(num) && isFinite(num)) {
      const decimalPower = 10 ** decimals;
      return Math.round(num * decimalPower) / decimalPower;
    }
    return NaN;
  }

  static isZero(n: number): boolean {
    return n < 0.0001 && n > -0.0001;
  }

  static clamp(n: number, min: number, max: number): number {
    if (n < min) return min;
    if (n > max) return max;
    return n;
  }


  static mod(x: number, n: number): number {
    return ((x % n) + n) % n;
  }

  static intDiv(a: number, b: number): number {
    const result = a / b;
    if (result >= 0) return Math.floor(result);
    else return Math.ceil(result);
  }

  static lerp(from: number, to: number, factor: number): number {
    return from * (1.0 - factor) + to * factor;
  }

  static calcGpsDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const earthRadiusKm = 6371;

    const dLat = MathUtil.deg2rad(lat2 - lat1);
    const dLon = MathUtil.deg2rad(lon2 - lon1);

    lat1 = MathUtil.deg2rad(lat1);
    lat2 = MathUtil.deg2rad(lat2);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusKm * c;
  }

  static delete<T>(obj: T, key: keyof T): void {
    delete obj[key];
  }

  static ensureArrayWithSize<T>(array: T[] | undefined, size: number, itemToAdd: T | ((index: number) => T)): T[] {
    if (array && array.length >= size) return array;
    const cloned = array ? [...array] : [];
    while (cloned.length < size) cloned.push(Util.isFunction(itemToAdd) ? itemToAdd(cloned.length) : itemToAdd);
    return cloned;
  }

  static groupBy<T, K extends keyof T>(xs: T[], key: K): Record<string | number, T[]> {
    return xs.reduce<any>(function (rv, x) {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
  }
}

export function getUrlParameter(urlString: string, param: string): string | null {
  const url = new URL(urlString);
  return url.searchParams.get(param);
}

export function getObjectDiff(a: any, b: any): string[] {
  return reduce(a, (result: any, value: any, key: any): any[] => {
    return isEqual(value, b[key]) ?
      result : result.concat(key);
  }, []);
  // → [ "prop2" ]
}

export function findLastIndex<T>(array: T[], predicate: (element: T) => boolean): number {
  for (let i = array.length - 1; i >= 0; i--) {
    if (predicate(array[i])) {
      return i;
    }
  }
  return -1; // element not found in array
}

export const deepEqual = isEqual;
export { cloneDeep, debounce, throttle };
